import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  Brain, 
  MessageSquare, 
  FileSearch, 
  Clock,
  AlertTriangle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const AIInsights = ({ requestId, requestData }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [generatingResponses, setGeneratingResponses] = useState(false);

  const { API } = useContext(AuthContext);

  useEffect(() => {
    if (requestId) {
      fetchInsights();
    }
  }, [requestId]);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API}/ai/request-insights/${requestId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInsights(data);
      }
    } catch (error) {
      console.error('Error fetching AI insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const classifyRequest = async () => {
    setClassifying(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API}/ai/classify-request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ request_id: requestId })
      });

      if (response.ok) {
        const classification = await response.json();
        toast.success('Request classified successfully!');
        fetchInsights(); // Refresh insights
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Classification failed');
      }
    } catch (error) {
      console.error('Error classifying request:', error);
      toast.error('Failed to classify request');
    } finally {
      setClassifying(false);
    }
  };

  const generateResponses = async () => {
    setGeneratingResponses(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API}/ai/generate-responses`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          request_id: requestId,
          context: "Generate professional responses for this police records request"
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Response suggestions generated!');
        fetchInsights(); // Refresh insights
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Response generation failed');
      }
    } catch (error) {
      console.error('Error generating responses:', error);
      toast.error('Failed to generate responses');
    } finally {
      setGeneratingResponses(false);
    }
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return <Badge className={variants[priority] || variants.medium}>{priority?.toUpperCase()}</Badge>;
  };

  const getComplexityIcon = (complexity) => {
    switch (complexity) {
      case 'simple': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'complex': return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default: return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-6">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Loading AI insights...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-purple-600" />
            AI-Powered Insights
          </CardTitle>
          <CardDescription>
            Enhance request processing with artificial intelligence
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <Button 
              onClick={classifyRequest} 
              disabled={classifying}
              size="sm"
              variant="outline"
            >
              {classifying ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Brain className="h-4 w-4 mr-2" />
              )}
              Classify Request
            </Button>
            
            <Button 
              onClick={generateResponses} 
              disabled={generatingResponses}
              size="sm"
              variant="outline"
            >
              {generatingResponses ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <MessageSquare className="h-4 w-4 mr-2" />
              )}
              Generate Responses
            </Button>
          </div>

          {insights?.classification && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Brain className="h-5 w-5" />
                AI Classification
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Priority:</span>
                  {getPriorityBadge(insights.classification.priority)}
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="font-medium">Complexity:</span>
                  <div className="flex items-center gap-2">
                    {getComplexityIcon(insights.classification.complexity)}
                    <span className="capitalize">{insights.classification.complexity}</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="font-medium">Estimated Days:</span>
                  <Badge variant="outline">{insights.classification.estimated_days} days</Badge>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="font-medium">Category:</span>
                  <Badge variant="secondary">{insights.classification.category.replace('_', ' ')}</Badge>
                </div>
                
                {insights.classification.tags && insights.classification.tags.length > 0 && (
                  <div>
                    <span className="font-medium">Tags:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {insights.classification.tags.map((tag, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                <div>
                  <span className="font-medium">Reasoning:</span>
                  <p className="text-sm text-gray-600 mt-1">{insights.classification.reasoning}</p>
                </div>
              </div>
            </div>
          )}

          {insights?.response_suggestions && insights.response_suggestions.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                AI Response Suggestions
              </h3>
              <div className="space-y-3">
                {insights.response_suggestions.map((suggestion, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline" className="capitalize">
                        {suggestion.type.replace('_', ' ')}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        {suggestion.tone}
                      </Badge>
                    </div>
                    <h4 className="font-medium mb-2">Subject: {suggestion.subject}</h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">
                      {suggestion.content}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {insights?.document_analyses && insights.document_analyses.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <FileSearch className="h-5 w-5" />
                Document Analysis
              </h3>
              <div className="space-y-3">
                {insights.document_analyses.map((analysis, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{analysis.filename}</span>
                      <Badge variant="outline">{analysis.document_type}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{analysis.summary}</p>
                    {analysis.key_info && analysis.key_info.length > 0 && (
                      <div>
                        <span className="font-medium text-sm">Key Information:</span>
                        <ul className="text-sm text-gray-600 mt-1 list-disc list-inside">
                          {analysis.key_info.map((info, i) => (
                            <li key={i}>{info}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!insights?.insights_available && (
            <div className="text-center py-8 text-gray-500">
              <Brain className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No AI insights available yet.</p>
              <p className="text-sm">Use the buttons above to generate AI analysis.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AIInsights;